// ================================================================
// Layout.tsx — App Shell: Header + Tabs + Alert Bell
// ================================================================

import { useState, useRef, useEffect, ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { logout } from '../auth';
import { isDemoMode, disableDemoMode } from '../demo';
import { clearApiKeySession } from '../apikey-session';
import { C, shadow } from '../styles';

// ── Alert types ───────────────────────────────────────────────
export interface AlertItem {
  id: string;
  level: 'critical' | 'warning' | 'info';
  title: string;
  detail: string;
  time: string;
}

// Hardcoded alerts — in production these would come from GET /api/alerts
const INITIAL_ALERTS: AlertItem[] = [
  { id: 'a1', level: 'critical', title: 'DLP 敏感資料外洩疑慮', detail: 'alice@contoso.com 在 Copilot 中輸入含信用卡號的內容', time: '10 分鐘前' },
  { id: 'a2', level: 'critical', title: 'DLP 觸發：機密文件', detail: 'bob@contoso.com 上傳 NDA.docx 至外部 AI 服務', time: '23 分鐘前' },
  { id: 'a3', level: 'warning',  title: 'MDA Token 即將過期', detail: 'Defender for Cloud Apps 憑證將於 3 天後失效', time: '1 小時前' },
  { id: 'a4', level: 'warning',  title: '缺少 Graph 授權', detail: 'northwind.com 尚未完成 Admin Consent，資料無法擷取', time: '2 小時前' },
  { id: 'a5', level: 'info',     title: '新 AI 服務偵測', detail: '偵測到 Perplexity.ai 首次存取，建議加入監控清單', time: '昨天' },
];

const LEVEL_COLOR: Record<AlertItem['level'], string> = {
  critical: C.red,
  warning:  C.yellow,
  info:     C.blue,
};

// ── Nav tabs ──────────────────────────────────────────────────
const TABS = [
  { to: '/',         label: '📊 使用總覽', end: true  },
  { to: '/activity', label: '📋 活動紀錄', end: false },
  { to: '/audit',    label: '🔍 互動稽核', end: false },
  { to: '/agents',   label: '🤖 Agent 管理', end: false },
  { to: '/settings', label: '⚙️ 設定',    end: false },
];

// ── Layout ────────────────────────────────────────────────────
interface LayoutProps { children: ReactNode }

export default function Layout({ children }: LayoutProps) {
  const { account } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AlertItem[]>(INITIAL_ALERTS);
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  const unread = alerts.length;

  // Close panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dismiss = (id: string) => setAlerts(prev => prev.filter(a => a.id !== id));
  const dismissAll = () => setAlerts([]);

  const handleLogout = async () => {
    if (isDemoMode()) {
      disableDemoMode();
      clearApiKeySession();
      window.location.reload();
      return;
    }
    await logout();
    navigate('/');
    window.location.reload();
  };

  const isDemo = isDemoMode();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: C.bg0 }}>
      {/* ── Demo banner ── */}
      {isDemo && (
        <div style={{
          background: `linear-gradient(90deg, ${C.purple}cc, ${C.blue}cc)`,
          color: '#fff', fontSize: 12, fontWeight: 600,
          padding: '6px 24px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>🎮 Demo 模式 — 顯示模擬資料，不連接任何真實帳號或 Entra</span>
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.4)',
              color: '#fff', borderRadius: 6, padding: '3px 12px', fontSize: 11,
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            退出 Demo
          </button>
        </div>
      )}
      {/* ── Header ── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', height: 56,
        background: C.bg1, borderBottom: `1px solid ${C.border}`,
        position: 'sticky', top: 0, zIndex: 100, boxShadow: shadow,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🛡️</span>
          <span style={{ color: C.t1, fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px' }}>
            AI Governance
          </span>
          <span style={{
            background: C.blue + '22', color: C.blue, border: `1px solid ${C.blue}44`,
            borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700,
          }}>PREVIEW</span>
        </div>

        {/* Tabs */}
        <nav style={{ display: 'flex', gap: 4 }}>
          {TABS.map(({ to, label, end }) => (
            <NavLink
              key={to} to={to} end={end}
              style={({ isActive }) => ({
                padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                textDecoration: 'none',
                background: isActive ? C.blue + '22' : 'transparent',
                color: isActive ? C.blue : C.t3,
                border: isActive ? `1px solid ${C.blue}44` : '1px solid transparent',
                transition: 'all .15s',
              })}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Right controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Alert bell */}
          <div ref={bellRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setOpen(o => !o)}
              style={{
                background: open ? C.blue + '22' : 'transparent',
                border: `1px solid ${open ? C.blue + '44' : C.border}`,
                borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
                color: C.t2, fontSize: 16, position: 'relative',
              }}
              aria-label="通知"
            >
              🔔
              {unread > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: C.red, color: '#fff',
                  borderRadius: 10, fontSize: 10, fontWeight: 700,
                  padding: '1px 5px', minWidth: 16, textAlign: 'center',
                  border: `2px solid ${C.bg1}`,
                }}>
                  {unread}
                </span>
              )}
            </button>

            {open && (
              <div style={{
                position: 'absolute', right: 0, top: 44,
                width: 380, background: C.bg1, border: `1px solid ${C.border}`,
                borderRadius: C.radiusLg, boxShadow: shadow, zIndex: 200,
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
                }}>
                  <span style={{ color: C.t1, fontWeight: 600, fontSize: 14 }}>
                    通知 {unread > 0 && <span style={{ color: C.t3, fontWeight: 400 }}>({unread})</span>}
                  </span>
                  {unread > 0 && (
                    <button onClick={dismissAll} style={{
                      background: 'none', border: 'none', color: C.t3,
                      fontSize: 12, cursor: 'pointer',
                    }}>全部清除</button>
                  )}
                </div>

                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {alerts.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: C.t4, fontSize: 13 }}>
                      無未讀通知
                    </div>
                  ) : alerts.map(a => (
                    <div key={a.id} style={{
                      display: 'flex', gap: 10, padding: '12px 16px',
                      borderBottom: `1px solid ${C.border}22`,
                    }}>
                      <span style={{ fontSize: 16, marginTop: 2 }}>
                        {a.level === 'critical' ? '🚨' : a.level === 'warning' ? '⚠️' : 'ℹ️'}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: LEVEL_COLOR[a.level], fontWeight: 600, fontSize: 13,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {a.title}
                        </div>
                        <div style={{ color: C.t3, fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>
                          {a.detail}
                        </div>
                        <div style={{ color: C.t4, fontSize: 11, marginTop: 4 }}>{a.time}</div>
                      </div>
                      <button onClick={() => dismiss(a.id)} style={{
                        background: 'none', border: 'none', color: C.t4,
                        cursor: 'pointer', fontSize: 16, flexShrink: 0, alignSelf: 'flex-start',
                      }}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* User info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: C.blue + '33', border: `1px solid ${C.blue}55`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.blue, fontWeight: 700, fontSize: 13,
            }}>
              {(account?.name?.[0] ?? account?.username?.[0] ?? '?').toUpperCase()}
            </div>
            <span style={{ color: C.t2, fontSize: 13, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {account?.name ?? account?.username}
            </span>
            <button onClick={handleLogout} style={{
              background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
              color: C.t3, fontSize: 12, padding: '4px 8px', cursor: 'pointer',
            }}>登出</button>
          </div>
        </div>
      </header>

      {/* ── Page content ── */}
      <main style={{ flex: 1, padding: '24px 28px', maxWidth: 1440, margin: '0 auto', width: '100%' }}>
        {children}
      </main>
    </div>
  );
}
